import type { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { financingPageComponentsMap } from "../../blocks/componentMaps";
import { Template as FinancingForm } from "../../templates/FinancingFormTemplate";
import { Template as FinancingPreQualTrigger } from "../../templates/FinancingPreQualTriggerTemplate";
import { OrderSupportLayout } from "../OrderSupportLayout";

import styles from "./index.module.scss";

const richTextUnwrap = (value: string): string =>
    value.replace(/class=.rich-text./g, "");

const FinancingCTASection = (
    props: Immutable<{
        applyNowText: string;
        showPrequal: boolean;
    }>,
): React.JSX.Element => (
    <div
        className={concatClassNames([
            styles.heroButtonContainer,
            styles.heroButtonContainerFinance,
        ])}
    >
        {props.showPrequal ? (
            <div>
                <p className="caption-light">
                    Check if you pre-qualify
                    <br />
                    without affecting your credit score.
                </p>
                <FinancingPreQualTrigger />
            </div>
        ) : null}
        <div>
            <p className="caption-light">
                Apply now and determine
                <br />
                your specific credit limit.
            </p>
            <FinancingForm
                link_class={`${styles.financingOfferCtaApply} al-financing-page__apply-now-button`}
                link_title={props.applyNowText}
                link_text={props.applyNowText}
                application_source="Financing Page"
            />
        </div>
    </div>
);

export const FinancingPageBody = (
    props: Immutable<{
        page: templates.FinancingPageTemplate["page"];
        prequal_settings: templates.FinancingPageTemplate["prequal_settings"];
    }>,
): React.JSX.Element => {
    const applyNowText = t`Apply Now`;
    const components = {
        ...financingPageComponentsMap,
        CTA_section: () => (
            <FinancingCTASection
                applyNowText={applyNowText}
                showPrequal={props.prequal_settings.financing_page}
            />
        ),
    };

    return (
        <OrderSupportLayout
            id="support"
            centered
            className={styles.orderSupport}
            detailClassName={styles.detailContainer}
        >
            <section className={styles.heroContainer}>
                <div
                    className={styles.topLine}
                    dangerouslySetInnerHTML={{
                        __html: richTextUnwrap(
                            props.page.hero_description.html,
                        ),
                    }}
                />
                <div
                    className={styles.header}
                    dangerouslySetInnerHTML={{
                        __html: richTextUnwrap(props.page.hero_title.html),
                    }}
                />
                <p>
                    <a href="#terms_and_conditions">FINANCING TERMS</a>
                </p>
            </section>
            <section className={styles.bodyContainer}>
                {props.page.body ? (
                    <StreamField
                        tagName={React.Fragment}
                        value={props.page.body.value}
                        components={components}
                    />
                ) : null}
            </section>
            <section className={styles.financingFooterContainer}>
                <div className={styles.financingOffer}>
                    <FinancingForm
                        link_class={`${styles.financingOfferCtaApply} al-financing-page__apply-now-button-2`}
                        link_title={applyNowText}
                        link_text={applyNowText}
                        application_source="Financing Page"
                    />
                </div>
            </section>
        </OrderSupportLayout>
    );
};
